import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { floral, useMultiStyleConfig } from "@thebouqs/system";
import { toRange } from "@thebouqs/utils";
var PRELOAD_BACK = 1;
var PRELOAD_FORWARD = 2;
var VELOCITY_TRACKING = 0.2;
var STEP = 0.18;
export var Slider = _ref => {
  var {
    slides,
    index,
    span = 1,
    seamless,
    goTo
  } = _ref;
  // ref is necessary so the animate function has access to the current length
  var length = useRef(slides.length);
  var focused = useRef(0); // sliding motion

  var slide = useRef({
    source: index,
    position: index,
    destination: index,
    dragStartX: 0,
    dragStartTime: null,
    dragVelocity: 0,
    distance: 0,
    wait: null
  }); // detect end of a single or multi-span slideshow

  var spanAdjustment = span - 1; // offset === distance from snapping point (slide start)

  var [offset, setOffset] = useState(0);
  var el = useRef(null);
  var styles = useMultiStyleConfig("Carousel", {});
  var animate = useCallback(() => {
    if (slide.current.wait) {
      return;
    }

    var _slide = slide.current;
    _slide.wait = requestAnimationFrame(() => {
      var _el$current$offsetWid, _el$current;

      _slide.wait = null;

      if (_slide.dragStartTime) {
        Object.assign(_slide, {
          position: _slide.destination,
          source: _slide.position
        });
        setOffset(_slide.destination);
        return;
      } // if transition hasn't been set up or destination got updated before finishing


      if (_slide.distance === 0 || _slide.destination !== _slide.source) {
        // begin transition
        var dist1 = _slide.destination - _slide.position; // intuitive direction

        if (seamless) {
          var aux = dist1 - length.current; // reverse distance

          var dist2;

          if (aux < -length.current) {
            // overflowing fw, cap to a +
            dist2 = toRange(aux, length.current);
          } else {
            // going bw, leave as is
            dist2 = aux;
          } // choose the shortest path


          if (Math.abs(dist2) < Math.abs(dist1)) {
            _slide.distance = dist2;
          } else {
            _slide.distance = dist1;
          }
        } else {
          // non-seamless
          _slide.distance = dist1;
        }

        _slide.source = slide.current.position;
      }

      if (Math.abs(_slide.distance * ((_el$current$offsetWid = (_el$current = el.current) == null ? void 0 : _el$current.offsetWidth) != null ? _el$current$offsetWid : 1)) > 10) {
        // animate left or right
        var motion = STEP * _slide.distance;
        Object.assign(_slide, {
          position: toRange(_slide.position + motion, length.current),
          distance: _slide.distance - motion,
          source: _slide.position
        }); // call a refresh on the slider DOM

        animate();
        setOffset(offset + motion);
      } else {
        // end transition
        var destination = toRange(slide.current.destination, length.current);
        Object.assign(_slide, {
          destination,
          position: destination,
          source: destination,
          distance: 0
        });
        setOffset(0);
      }
    });
  }, [offset, seamless]);
  useEffect(() => {
    Object.assign(slide.current, {
      destination: index,
      dragStartTime: null,
      source: slide.current.position
    });
    animate();
  }, [index, animate]);
  var onPointerDown = useCallback(event => {
    Object.assign(slide.current, {
      source: slide.current.position,
      destination: slide.current.position,
      dragStartX: event.touches[0].pageX,
      dragStartTime: Date.now(),
      dragVelocity: 0
    });
  }, []);
  var onPointerMove = useCallback(event => {
    var _el$current$offsetWid2, _el$current2, _slide$current$dragSt;

    if (slide.current.dragStartTime === null) {
      return;
    }

    var dragTo = event.touches[0].pageX;
    var distance = slide.current.dragStartX - dragTo;
    var unitDistance = distance / ((_el$current$offsetWid2 = (_el$current2 = el.current) == null ? void 0 : _el$current2.offsetWidth) != null ? _el$current$offsetWid2 : 1);
    var newPosition = slide.current.position + unitDistance;
    var now = Date.now();
    var elapsed = now - ((_slide$current$dragSt = slide.current.dragStartTime) != null ? _slide$current$dragSt : 0);
    Object.assign(slide.current, {
      // thethered position refresh
      position: slide.current.source,
      // stopping point for drag animation, becomes source when pointer releases
      destination: newPosition,
      // update before next motion capture
      distance,
      dragStartX: dragTo,
      dragStartTime: now,
      dragVelocity: distance / elapsed * VELOCITY_TRACKING * -1000 + (1 - VELOCITY_TRACKING) * slide.current.dragVelocity
    });
    animate();
  }, [animate]);
  var onPointerOut = useCallback(() => {
    var _slide$current$dragSt2, _slide$current, _slide$current2;

    var direction;
    var dragging = Date.now() - ((_slide$current$dragSt2 = slide == null ? void 0 : (_slide$current = slide.current) == null ? void 0 : _slide$current.dragStartTime) != null ? _slide$current$dragSt2 : 0) < 50;

    if (dragging && Math.abs(slide.current.dragVelocity) > 200) {
      // go to prev/next slide
      direction = slide.current.dragVelocity > 0 ? -1 : 1;
    } else {
      // no traceable action, don't move
      direction = 0;
    }

    Object.assign(slide.current, {
      // index to activate next
      destination: Math.round(toRange(slide.current.source + direction, slides.length)),
      dragStartTime: null
    });
    goTo((_slide$current2 = slide.current) == null ? void 0 : _slide$current2.destination);
  }, [goTo, slides.length]); // anticipate index and offset to smoothly swap visible slides
  // offsetPosition handles when the user is mid-dragging

  var offsetPosition;

  if (focused.current > length.current - span) {
    offsetPosition = length.current - span;
  } else if (seamless) {
    offsetPosition = slide.current.position;
  } else {
    offsetPosition = toRange(slide.current.position, length.current);
  } // which slide should we snap to the viewport


  var newIndex = Math.round(offsetPosition);
  var a11y = useCallback(ev => {
    var dest = parseInt(ev.currentTarget.dataset.i, 10);
    focused.current = dest;
    goTo(dest != null ? dest : index);
  }, [goTo, index]);
  var preloadedSlides = useMemo(() => {
    // create nodes for lazy-loaded slides
    length.current = slides.length;
    var loaded = []; // newIndex will take into account that we may jump further than the preloaded range
    // it lets us go jump slide by slide, as opposed to index, which is the final destination

    for (var i = newIndex - PRELOAD_BACK; i <= newIndex + spanAdjustment + PRELOAD_FORWARD; i++) {
      // circle back to preload the other end of the slider
      // example: push from slides[5] to slides[1] from an array of 6 slides
      var slideIndex = (i + length.current) % length.current;
      loaded.push( /*#__PURE__*/React.createElement(floral.li, {
        sx: styles.slide,
        "data-i": slideIndex,
        key: "slide-" + i + "-" + slideIndex,
        onFocus: a11y,
        className: "carousel-slide",
        flexBasis: 100 / span + "%",
        tabIndex: !focused.current && slideIndex === 0 || Math.abs(focused.current - slideIndex) <= 1 ? 0 : -1
      }, slides[slideIndex]));
    }

    return loaded;
  }, [newIndex, slides, a11y, span, spanAdjustment, styles.slide]); // distance between offset and snapping target

  var newOffset = newIndex - offsetPosition; // ToDo: when tabbing, the browser will shift the list to left:0 for some reason
  // offset is expressed in fractions of slides, convert it to percentage

  var translateX = {
    transform: "translateX(" + (newOffset - PRELOAD_BACK) * (100 / span) + "%)"
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: el,
    className: "carousel-viewport",
    onTouchStart: onPointerDown,
    onTouchMove: onPointerMove,
    onTouchEnd: onPointerOut,
    style: {
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(floral.ul, {
    sx: styles.slider,
    className: "carousel-slides",
    style: translateX
  }, preloadedSlides));
};
//# sourceMappingURL=Slider.js.map